import { useCallback, useEffect, useRef, useState } from 'react';

type Offset = { x: number; y: number };

const STORAGE_KEY = 'approval-ai-assistant:panel-offset';
const VIEWPORT_MARGIN = 8;

const readStored = (): Offset => {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw) as Partial<Offset>;
    return {
      x: Number.isFinite(parsed.x) ? (parsed.x as number) : 0,
      y: Number.isFinite(parsed.y) ? (parsed.y as number) : 0,
    };
  } catch {
    return { x: 0, y: 0 };
  }
};

/**
 * 플로팅 패널을 헤더로 끌어 옮기는 훅. 기준 위치(CSS right/bottom)에서의 이동량만 transform 으로 더하고,
 * 놓을 때 뷰포트 밖으로 나간 만큼 되돌린다. 이동량은 localStorage 에 남겨 다시 열어도 같은 자리에 뜬다.
 *
 * 드래그 핸들 = 프레임 첫 자식(AiChatView Root)의 첫 자식(헤더). 헤더 안의 버튼은 제외한다.
 */
const useDraggablePanel = (
  frameRef: React.RefObject<HTMLDivElement | null>,
) => {
  const [offset, setOffset] = useState<Offset>(readStored);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    base: Offset;
  } | null>(null);

  // 창 크기가 바뀌어 패널이 화면 밖으로 나갔으면 안으로 당긴다
  const clamp = useCallback(
    (next: Offset): Offset => {
      const el = frameRef.current;
      if (!el) return next;
      const rect = el.getBoundingClientRect();
      // 현재 transform 을 뺀 기준 위치
      const baseLeft = rect.left - offset.x;
      const baseTop = rect.top - offset.y;
      const minX = VIEWPORT_MARGIN - baseLeft;
      const maxX = window.innerWidth - rect.width - VIEWPORT_MARGIN - baseLeft;
      const minY = VIEWPORT_MARGIN - baseTop;
      const maxY = window.innerHeight - rect.height - VIEWPORT_MARGIN - baseTop;
      return {
        x: Math.min(Math.max(next.x, minX), Math.max(minX, maxX)),
        y: Math.min(Math.max(next.y, minY), Math.max(minY, maxY)),
      };
    },
    [frameRef, offset.x, offset.y],
  );

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const isOnHandle = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      if (target.closest('button, input, textarea, a')) return false;
      const handle = el.firstElementChild?.firstElementChild;
      return !!handle && handle.contains(target);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnHandle(e.target)) return;
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        base: offset,
      };
      el.setPointerCapture(e.pointerId);
      setIsDragging(true);
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      setOffset({
        x: drag.base.x + (e.clientX - drag.startX),
        y: drag.base.y + (e.clientY - drag.startY),
      });
    };

    const onPointerUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      dragRef.current = null;
      setIsDragging(false);
      if (el.hasPointerCapture(e.pointerId))
        el.releasePointerCapture(e.pointerId);
      setOffset((current) => {
        const clamped = clamp(current);
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped));
        } catch {
          // 저장 실패는 무시 — 이번 세션 위치만 유지
        }
        return clamped;
      });
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [frameRef, offset, clamp]);

  // 처음 열릴 때 저장된 위치가 현재 창 밖이면 안으로 (마운트 시 1회)
  const clampRef = useRef(clamp);
  clampRef.current = clamp;
  useEffect(() => {
    setOffset((current) => clampRef.current(current));
  }, []);

  return { offset, isDragging };
};

export default useDraggablePanel;
